import { Box, Card, Divider, Group, List, SimpleGrid, Table, Text, Title } from '@mantine/core';
import { BNButton } from '../../components/Button/Button';
import GroupIcon from '../../components/icons/Group';
import GroupsIcon from '../../components/icons/Groups';
import PersonIcon from '../../components/icons/Person';
import PriorityIcon from '../../components/icons/Priority';
import { AdminContentHeader } from '../../hoc/Admin/AdminContentHeader';
import { useBilling } from '../../hoc/Admin/Billing.hooks';
import {
  BrokerNerdsBarkerCoreEnumsUserRole,
  BrokerNerdsBarkerCoreModelsAdministrativeTenantPlan,
  getGetApiAccountingPlansCurrentQueryKey,
  getGetApiAccountingPlansFutureQueryKey,
  usePostApiTenantsTenantIdPlan,
} from '../../api';
import { formatCurrency } from '../../utils/formatters';
import classes from './Plan.module.css';
import { isAdminHosted, supportEmail } from '../../utils/whitelabel-consts';
import dayjs from 'dayjs';
import pluralize from 'pluralize';
import { useGlobalState } from '../../data/Global.state';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

function generateBillingDescriptor(tenantPlan: BrokerNerdsBarkerCoreModelsAdministrativeTenantPlan) {
  const { plan } = tenantPlan;

  if (plan.planId === 2) {
    // Free trial
    const daysRemaining = dayjs(tenantPlan.expiresAt).diff(dayjs(), 'day');
    return `Expires in ${daysRemaining} ${pluralize('day', daysRemaining)}`;
  }

  if (plan.planTypeId === 'FixedRate') {
    return `${formatCurrency(plan.value)}/month`;
  }

  if (plan.planTypeId === 'PercentageOfAutoPricedSales') {
    return `${(plan.value * 100).toFixed(2)}%/sale`;
  }

  if (plan.planTypeId === 'PercentageOfSales') {
    return `${(plan.value * 100).toFixed(2)}%/sale`;
  }

  if (plan.planTypeId === 'SalesBrackets') {
    return 'Tiered Rates';
  }

  return 'Unknown';
}

function generateFutureBillingDescriptor(startedAt: Date) {
  const daysUntilStart = Math.max(dayjs(startedAt).startOf('day').diff(dayjs().startOf('day'), 'day'), 0);

  return `Begins in ${daysUntilStart} ${pluralize('day', daysUntilStart)}`;
}

function PlanCard({ tenantPlan, billingDescriptor }: { tenantPlan: BrokerNerdsBarkerCoreModelsAdministrativeTenantPlan; billingDescriptor: string }) {
  return (
    <Card px="xl" pb="xl" withBorder radius="xs" maw={480} mt={32} className={classes.card}>
      <Box>
        <Group justify="space-between">
          <Title order={4}>{tenantPlan.plan.name}</Title>
          <Text c="var(--colors-brandcolor-5)" fw="bold">
            {billingDescriptor}
          </Text>
        </Group>
        <Divider my={16} mx={-24} color="var(--colors-darkPaper)" />
        <Group wrap="nowrap" align="center" gap="sm">
          <Group align="center" c="var(--colors-iconFill)">
            <PersonIcon size={24} />
          </Group>
          <Text c="var(--colors-gray-5)" size="xs">
            {tenantPlan.plan.description}
          </Text>
        </Group>
        <Divider variant="dotted" my={16} mr={-24} color="var(--colors-darkPaper)" />
        <List center spacing="xs" size="sm" icon={<PriorityIcon color="var(--colors-iconFill)" size={24} />}>
          <List.Item>Manual & Auto-Pricing</List.Item>
          <List.Item>SeatGeek, Ticketmaster, and TEVO Marketplaces</List.Item>
          <List.Item>Historical Trend Data</List.Item>
        </List>
      </Box>
      <Text bg="var(--colors-paperHover)" c="gray.7" p="sm" py="sm" size="xs" mt="xl" ta="center" style={{ borderRadius: 3 }}>
        Please contact{' '}
        <Text component="a" href={`mailto:${supportEmail}`} td="underline" c="var(--colors-brandcolor-5)" inline>
          {supportEmail}
        </Text>{' '}
        with any questions.
      </Text>
    </Card>
  );
}

export default function Plan() {
  const STANDARD_PLAN_ID = 3;
  const FREE_TRIAL_PLAN_ID = 2;
  const backlinkTo = location.pathname.replace(/\/plan/, '');
  const { currentPlan, futurePlan, isCurrentPlanLoading, isFuturePlanLoading, tenantUsers } = useBilling();
  const { tenantId } = useParams();
  const queryClient = useQueryClient();
  const { principal } = useGlobalState();
  const { mutateAsync: activatePlan, isPending: isActivatingPlan } = usePostApiTenantsTenantIdPlan();

  const currentUser = tenantUsers?.find((user) => user.principalId === principal?.principalId);
  const isGlobalAdmin = currentUser?.roleId === BrokerNerdsBarkerCoreEnumsUserRole.GlobalAdmin;
  const isCurrentPlanFreeTrial = currentPlan?.plan?.planId === FREE_TRIAL_PLAN_ID;
  const hasNoCurrentPlan = !currentPlan?.plan;
  const hasFuturePlan = !!futurePlan?.plan;
  const showActivateButton =
    !isCurrentPlanLoading && !isFuturePlanLoading && !!tenantId && isGlobalAdmin && (isCurrentPlanFreeTrial || hasNoCurrentPlan) && !hasFuturePlan && isAdminHosted;

  const onActivate = async () => {
    if (!tenantId) {
      return;
    }

    await activatePlan({ tenantId, data: { planId: STANDARD_PLAN_ID } });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getGetApiAccountingPlansCurrentQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getGetApiAccountingPlansFutureQueryKey() }),
    ]);
  };

  return (
    <>
      <AdminContentHeader
        sticky
        title="Plan Details"
        backlinkTo={backlinkTo}
        backlinkTitle="Billing Overview"
        rightSection={
          showActivateButton ? (
            <BNButton
              size="xs"
              variant="filled"
              color="green"
              onClick={async () => {
                await onActivate();
              }}
              loading={isActivatingPlan}
            >
              Activate
            </BNButton>
          ) : undefined
        }
      />

      {currentPlan?.plan && (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
          <Box>
            <PlanCard tenantPlan={currentPlan} billingDescriptor={generateBillingDescriptor(currentPlan)} />
            {futurePlan && <PlanCard tenantPlan={futurePlan} billingDescriptor={generateFutureBillingDescriptor(futurePlan.startedAt)} />}
            {/* <Rates /> */}
            {/* <PlanOptions /> */}
          </Box>
        </SimpleGrid>
      )}
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function Rates() {
  return (
    <Card px="xl" pb="xl" withBorder radius="xs" maw={480} mt={32} className={classes.card}>
      <Box>
        <Group justify="space-between">
          <Title order={4}>Rates</Title>
          <Group gap="xs">
            <Box h={16} w={16} bg="var(--colors-brandcolor-5)" className={classes.rateMarker} />
            <Text c="var(--colors-gray-7)" size="xs">
              Your Projected Rate
            </Text>
          </Group>
        </Group>
        <Divider my={16} mx={-24} color="var(--colors-darkPaper)" />
        <Table mx={-24} w="calc(100% + 48px)" mt={-16} mb={-24} striped className={classes.rateTable}>
          <thead>
            <tr>
              <th>Monthly Usage</th>
              <th>Rate</th>
            </tr>
          </thead>
          <tbody>
            {/* add className="projected-rate" to any <tr> here to give it the projected/highligted style */}
            <tr className="projected-rate">
              <td>Any</td>
              <td>1.49%</td>
            </tr>
            <tr>
              <td>$41,667</td>
              <td>1.42%</td>
            </tr>
            <tr>
              <td>$83,333</td>
              <td>1.19%</td>
            </tr>
            <tr>
              <td>$166,667</td>
              <td>0.97%</td>
            </tr>
            <tr>
              <td>$333,333</td>
              <td>0.82%</td>
            </tr>
            <tr>
              <td>$500,000</td>
              <td>0.78%</td>
            </tr>
            <tr>
              <td>$750,000</td>
              <td>0.74%</td>
            </tr>
            <tr>
              <td>$1,000,000</td>
              <td>0.48%</td>
            </tr>
            <tr>
              <td>$2,500,000</td>
              <td>0.37%</td>
            </tr>
            <tr>
              <td>$5,000,000+</td>
              <td>Contact Us</td>
            </tr>
          </tbody>
        </Table>
      </Box>
    </Card>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function PlanOptions() {
  return (
    <SimpleGrid cols={3} spacing="xl" hidden>
      <Card px="xl" pb="xl" withBorder radius="xs" className={classes.card}>
        <Box>
          <Group justify="space-between">
            <Title order={4}>Starter</Title>
            <Text c="var(--colors-brandcolor-5)" fw="bold">
              $10/mo
            </Text>
          </Group>
          <Divider my={16} mx={-24} color="var(--colors-divider)" />
          <Group wrap="nowrap" align="center" gap="sm">
            <Group align="center" c="var(--colors-iconFill)">
              <PersonIcon size={24} />
            </Group>
            <Text c="var(--colors-gray-5)" size="xs">
              Made for solo brokers with less than $250k of monthly volume.
            </Text>
          </Group>
          <Divider variant="dotted" my={16} mr={-24} color="var(--colors-divider)" />
          <List center size="sm" icon={<PriorityIcon color="var(--colors-iconFill)" size={24} />}>
            <List.Item>Feature number one</List.Item>
            <List.Item>Feature number two</List.Item>
            <List.Item>Feature number three</List.Item>
          </List>
        </Box>
        <Box>
          <BNButton variant="filled" color="brandcolor" fullWidth size="sm" disabled>
            Current Plan
          </BNButton>
        </Box>
      </Card>
      <Card px="xl" pb="xl" withBorder radius="xs" className={classes.card}>
        <Box>
          <Group justify="space-between">
            <Title order={4}>Team</Title>
            <Text c="var(--colors-brandcolor-5)" fw="bold">
              $10/mo
            </Text>
          </Group>
          <Divider my={16} mx={-24} color="var(--colors-divider)" />
          <Group wrap="nowrap" align="center" gap="sm">
            <Group align="center" c="var(--colors-iconFill)">
              <GroupIcon size={24} />
            </Group>
            <Text c="var(--colors-gray-5)" size="xs">
              Made for small teams of brokers with less than $250k of monthly volume.
            </Text>
          </Group>
          <Divider variant="dotted" my={16} mr={-24} color="var(--colors-divider)" />
          <List center size="sm" icon={<PriorityIcon color="var(--colors-iconFill)" size={24} />}>
            <List.Item>Feature number one</List.Item>
            <List.Item>Feature number two</List.Item>
            <List.Item>Feature number three</List.Item>
            <List.Item>Feature number four</List.Item>
          </List>
        </Box>
        <Box>
          <BNButton variant="filled" color="brandcolor" fullWidth size="sm">
            Switch to Team
          </BNButton>
        </Box>
      </Card>
      <Card px="xl" pb="xl" withBorder radius="xs" className={classes.card}>
        <Box>
          <Group justify="space-between">
            <Title order={4}>Enterprise</Title>
            <Text c="var(--colors-brandcolor-5)" fw="bold">
              $10/mo
            </Text>
          </Group>
          <Divider my={16} mx={-24} color="var(--colors-divider)" />
          <Group wrap="nowrap" align="center" gap="sm">
            <Group align="center" c="var(--colors-iconFill)">
              <GroupsIcon size={24} />
            </Group>
            <Text c="var(--colors-gray-5)" size="xs">
              Made for large teams of brokers with more than $250k of monthly volume.
            </Text>
          </Group>
          <Divider variant="dotted" my={16} mr={-24} color="var(--colors-divider)" />
          <List center size="sm" pb="sm" mb={36} icon={<PriorityIcon color="var(--colors-iconFill)" size={24} />}>
            <List.Item>Feature number one</List.Item>
            <List.Item>Feature number two</List.Item>
            <List.Item>Feature number three</List.Item>
            <List.Item>Feature number four</List.Item>
            <List.Item>Feature number five</List.Item>
          </List>
        </Box>
        <Box>
          <BNButton variant="filled" color="brandcolor" fullWidth size="sm">
            Switch to Enterprise
          </BNButton>
        </Box>
      </Card>
    </SimpleGrid>
  );
}
